import React, { useState, useEffect } from 'react';

/**
 * Brand-aware full-page loading indicator.
 *
 * Replaces the previous /loading.gif with a single brand phrase — same
 * footprint, same public API (`size` / `className`) so every caller
 * (Dashboard, FormBuilder, FormView, all integration pages, …) picks
 * the new look up automatically.
 *
 * Behavior:
 *   * One initial phrase is picked ONCE per page load (module scope).
 *     Every FFLoader that mounts during the same page refresh shows
 *     the same phrase — so a typical "auth-gate loader → dashboard
 *     fetch loader" sequence on a single page refresh shows just one
 *     copy line, not two.
 *   * Same idea for the long-wait apology phrase: one is picked once
 *     per page load and shared. After LONG_WAIT_MS (8s) every loader
 *     instance that's still mounted crossfades ONCE to that shared
 *     phrase and holds there.
 *   * Fades each line in/out via CSS opacity transitions — no extra
 *     animation library needed.
 *
 * Why a single component? Every previous caller already imports
 * `FFLoader` and passes `size`; swapping the implementation here means
 * we don't need to update 17+ pages. The `size` prop is reinterpreted
 * as the FONT size in pixels for the new text-based variant (legacy
 * size=48 → ~14px text, which matches the existing visual weight).
 */
const COPY_OPTIONS = [
  'Adding final touches…',
  'Tightening some screws…',
  'Squashing some bugs…',
  'Assembling the pieces…',
  'Calibrating the machinery…',
  'Sharpening the tools…',
  'Polishing the edges…',
  'Crunching the numbers…',
  'Processing the request…',
  'Training the hamsters…',
  'Connecting the dots…',
  'Optimizing the workflow…',
  'Weaving things together…',
  'Untangling the spaghetti…',
  'Tailoring the experience…',
  'Getting everything ready…',
  'Making things sparkle…',
  'Checking every detail…',
];

// Slightly apologetic copy bank — kicks in after the loader has been
// on-screen for LONG_WAIT_MS. Keeps the user company on long-running
// operations and signals that the app hasn't frozen.
const LONG_WAIT_COPY = [
  'Almost there, promise…',
  'This is taking longer than usual…',
  'Hang tight, still working…',
  'Just a few more seconds…',
  'Thanks for your patience…',
  'Working on it — it\u2019s a big one…',
  'Nearly done, hang in there…',
  'Pushing through the final stretch…',
];

const FADE_MS = 280;
const LONG_WAIT_MS = 8000;

const pickRandom = (pool, exclude) => {
  // Pick a line different from the current one. Used once for the
  // initial phrase and once for the long-wait phrase per page load.
  let next = pool[Math.floor(Math.random() * pool.length)];
  let attempts = 0;
  while (next === exclude && attempts < 8 && pool.length > 1) {
    next = pool[Math.floor(Math.random() * pool.length)];
    attempts += 1;
  }
  return next;
};

// Picked ONCE at module-import time → shared by every FFLoader instance
// that renders during the same page load. A page refresh re-evaluates
// this module and re-rolls. The long-wait phrase is rolled lazily on
// the first 8-second timeout (still shared via the module-scoped value
// once it's set) so the user doesn't see two unrelated apology lines if
// multiple FFLoaders are mounted in series.
const INITIAL_PHRASE = pickRandom(COPY_OPTIONS, null);
let SHARED_LONG_WAIT_PHRASE = null;
const getLongWaitPhrase = () => {
  if (!SHARED_LONG_WAIT_PHRASE) {
    SHARED_LONG_WAIT_PHRASE = pickRandom(LONG_WAIT_COPY, null);
  }
  return SHARED_LONG_WAIT_PHRASE;
};

const FFLoader = ({ size = 40, className = '' }) => {
  // `size` was the icon size in the legacy gif loader. We keep the
  // signature for backwards compat but scale it down to a sensible
  // font size (the old default of 40px → 13px text).
  const fontSize = Math.max(12, Math.round(size * 0.32));

  const [text, setText] = useState(INITIAL_PHRASE);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    // We deliberately do NOT rotate to a different upbeat phrase while
    // the loader is mounted — every load only ever shows the line it
    // picked on mount. Cycling phrases felt twitchy on fast loads (one
    // page = one quick mental anchor is calmer).
    //
    // Once we cross the LONG_WAIT_MS threshold we crossfade ONCE to a
    // long-wait phrase, then hold there. The user gets exactly two
    // states per render: their initial phrase, and (if the load is
    // slow) a single apology phrase.
    //
    // Both the outer "swap" timeout and the inner "fade in" timeout
    // need to be cancellable on unmount. We keep two SEPARATE handles
    // (a sibling variable for the inner one) rather than stashing the
    // inner handle on the outer one — browsers return a number from
    // setTimeout, not a Timeout object, so you can't attach properties.
    // Doing so threw "Cannot create property '_fadeIn' on number '3'".
    let fadeInHandle = null;
    const swapHandle = setTimeout(() => {
      // Fade out, swap to the shared long-wait phrase, fade in.
      setVisible(false);
      fadeInHandle = setTimeout(() => {
        setText(getLongWaitPhrase());
        setVisible(true);
      }, FADE_MS);
    }, LONG_WAIT_MS);

    return () => {
      clearTimeout(swapHandle);
      if (fadeInHandle) clearTimeout(fadeInHandle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={`inline-flex items-center justify-center gap-2 ${className}`}
      data-testid="ff-loader"
      role="status"
      aria-live="polite"
      aria-label={text}
    >
      {/* Three subtle pulsing dots — gives the text some kinetic anchor
          so it doesn't feel like a static label. CSS-only, no library. */}
      <span className="ff-loader-dots" aria-hidden="true">
        <span />
        <span />
        <span />
      </span>
      <span
        className="ff-loader-text"
        style={{
          fontSize,
          opacity: visible ? 1 : 0,
          transition: `opacity ${FADE_MS}ms ease-in-out`,
        }}
      >
        {text}
      </span>
      <style>{`
        .ff-loader-text {
          color: #52525B; /* zinc-600 */
          font-weight: 500;
          letter-spacing: 0.005em;
          white-space: nowrap;
        }
        .ff-loader-dots {
          display: inline-flex;
          gap: 4px;
        }
        .ff-loader-dots span {
          width: 6px;
          height: 6px;
          background-color: #18181B; /* zinc-900 */
          border-radius: 9999px;
          opacity: 0.35;
          animation: ff-loader-bounce 1.05s infinite ease-in-out;
        }
        .ff-loader-dots span:nth-child(1) { animation-delay: 0s; }
        .ff-loader-dots span:nth-child(2) { animation-delay: 0.18s; }
        .ff-loader-dots span:nth-child(3) { animation-delay: 0.36s; }
        @keyframes ff-loader-bounce {
          0%, 80%, 100% { transform: scale(0.7); opacity: 0.35; }
          40% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default FFLoader;
